import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { on } from "@ember/modifier";
import { fn, get } from "@ember/helper";
import eq from "truth-helpers/helpers/eq";
import { htmlSafe } from "@ember/template";

const COMPARISON_FEATURES = [
  { key: "bandwidthCost", label: "Bandwidth Cost (Monthly)" },
  { key: "costPerGB", label: "Cost per GB / IP" },
  { key: "connectionSurcharge", label: "Connection Surcharge" },
  { key: "totalMonthly", label: "Total Monthly Est." },
  { key: "quality", label: "Quality Tier" },
  { key: "features", label: "Features" },
];

const includes = (arr, val) => arr && arr.includes(val);

export default class ProxyviewsCalculator extends Component {
  @tracked bandwidth = 100;
  @tracked proxyType = "residential";
  @tracked connections = 100;
  @tracked regions = ["NA"];
  @tracked selectedProvider = "";
  @tracked autoCalc = true;

  constructor() {
    super(...arguments);
    const data = this.args.data;
    if (data && data.settings) {
      if (typeof data.settings.default_bandwidth_gb === "number") {
        this.bandwidth = data.settings.default_bandwidth_gb;
      }
      if (typeof data.settings.auto_calculate === "boolean") {
        this.autoCalc = data.settings.auto_calculate;
      }
      if (data.settings.default_proxy_type) {
        this.proxyType = data.settings.default_proxy_type;
      }
      if (typeof data.settings.max_connections === "number") {
        this._maxConnections = data.settings.max_connections;
      }
      if (typeof data.settings.max_vendors_compare === "number") {
        this._maxVendorsCompare = data.settings.max_vendors_compare;
      }
    }
  }

  get maxConnections() { return this._maxConnections || 1000; }
  get maxVendorsCompare() { return this._maxVendorsCompare || 5; }

  // Returns "per_ip" when the selected proxy type is datacenter or isp,
  // "per_gb" otherwise — drives the summary card label and unit suffix.
  get currentPricingUnit() {
    const ipTypes = ["datacenter", "isp"];
    return ipTypes.includes(this.proxyType) ? "per_ip" : "per_gb";
  }

  get activeRegions() {
    const data = this.args.data;
    if (!data || !data.region_multipliers) return [];

    const seen = new Set();
    const list = [];
    data.region_multipliers.forEach(r => {
      const code = r.code.toUpperCase();
      if (!seen.has(code)) {
        seen.add(code);
        list.push({
          code: code,
          name: r.name || code
        });
      }
    });
    return list;
  }

  get parsedData() {
    const data = this.args.data;
    if (!data) return { pricing: {}, providers: {} };

    const pricingData = {};
    const proxyTypes = ["residential", "datacenter", "mobile", "isp", "rotating"];
    proxyTypes.forEach((type) => {
      const labels = {
        residential: "Residential",
        datacenter: "Datacenter",
        mobile: "Mobile / 4G-5G",
        isp: "ISP (Static Residential)",
        rotating: "Rotating"
      };
      const thresholds = { residential: 500, datacenter: 200, mobile: 200, isp: 300, rotating: 400 };
      const perConn = { residential: 0.02, datacenter: 0.005, mobile: 0.04, isp: 0.015, rotating: 0.012 };
      pricingData[type] = {
        label: labels[type] || type,
        tiers: [],
        connectionFee: 0.0,
        connectionSurchargeThreshold: thresholds[type] || 300,
        connectionSurchargePerConn: perConn[type] || 0.02,
        regionMultipliers: {}
      };
    });

    if (data.pricing_tiers) {
      data.pricing_tiers.forEach((tier) => {
        const type = tier.proxy_type;
        if (pricingData[type]) {
          pricingData[type].tiers.push({
            tier: tier.tier || "Starter",
            minGB: (tier.min_gb !== null && tier.min_gb !== undefined) ? parseInt(tier.min_gb, 10) : 0,
            maxGB: (tier.max_gb !== null && tier.max_gb !== undefined) ? parseInt(tier.max_gb, 10) : null,
            pricePerGB: parseFloat(tier.price_per_gb)
          });
          if (tier.connections) {
            pricingData[type].connectionSurchargeThreshold = Math.max(
              pricingData[type].connectionSurchargeThreshold,
              parseInt(tier.connections, 10)
            );
          }
        }
      });
    }

    Object.keys(pricingData).forEach((type) => {
      pricingData[type].tiers.sort((a, b) => a.minGB - b.minGB);
    });

    if (data.region_multipliers) {
      data.region_multipliers.forEach((rm) => {
        const code = rm.code.toUpperCase();
        const mult = parseFloat(rm.multiplier);
        Object.keys(pricingData).forEach((type) => {
          if (!rm.proxy_type || rm.proxy_type === type) {
            pricingData[type].regionMultipliers[code] = mult;
          }
        });
      });
    }

    const providersData = {};
    proxyTypes.forEach((type) => {
      providersData[type] = [];
    });

    if (data.providers) {
      data.providers.forEach((p) => {
        const types = p.proxy_types || [p.proxy_type];
        types.forEach((type) => {
          if (providersData[type]) {
            const firstTierPrice = pricingData[type] && pricingData[type].tiers[0] ? pricingData[type].tiers[0].pricePerGB : 1.0;
            providersData[type].push({
              name: p.name,
              baseMultiplier: parseFloat(p.price_per_gb) / firstTierPrice || 1.0,
              quality: p.rating >= 4.5 ? "Premium" : p.rating >= 4.0 ? "High" : p.rating >= 3.5 ? "Good" : "Budget",
              features: p.supported_protocols ? p.supported_protocols.join(", ") : "Rotating sessions, SOCKS5 support",
              logo_url: p.logo_url,
              slug: p.slug,
              realPricePerGB: parseFloat(p.price_per_gb),
              pricingUnit: (p.type_pricing && p.type_pricing[type] && p.type_pricing[type].unit) || "per_gb",
              pricing_tiers: p.pricing_tiers || {},
              rating: p.rating || 0.0,
              uptime_percentage: p.uptime_percentage || 0.0,
              benchmark_score: p.benchmark_score || 0.0
            });
          }
        });
      });
    }

    return {
      pricing: pricingData,
      providers: providersData
    };
  }


  formatCurrency(value) {
    if (value === Infinity || isNaN(value) || value === undefined) return "—";
    return "$" + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  get availableProviders() {
    const { providers } = this.parsedData;
    return providers[this.proxyType] || [];
  }

  getProviderPricePerGB(p, bandwidth, globalTierLabel) {
    const tiers = p.pricing_tiers || {};
    const label = (globalTierLabel || "").toLowerCase();
    
    // 1. Direct match (e.g., "starter", "growth", "business", "enterprise")
    if (tiers[label] !== undefined && typeof tiers[label] !== 'string') {
      return parseFloat(tiers[label]);
    }
    
    // 2. Map global tier to common alternative naming styles
    const aliases = {
      starter: ["starter", "pay_as_you_go", "micro", "basic", "free"],
      growth: ["growth", "regular", "pro", "standard", "medium"],
      business: ["business", "advanced", "premium", "plus"],
      enterprise: ["enterprise", "custom", "corporate", "vip"]
    };
    
    const possibleKeys = aliases[label] || [];
    for (const key of possibleKeys) {
      if (tiers[key] !== undefined && typeof tiers[key] !== 'string') {
        return parseFloat(tiers[key]);
      }
    }
    
    // 3. Fallback to flat rate if it exists
    if (tiers["flat_rate"] !== undefined) {
      return parseFloat(tiers["flat_rate"]);
    }
    
    // 4. Default fallback: provider's base price_per_gb
    return parseFloat(p.realPricePerGB) || parseFloat(p.real_cost_per_gb) || parseFloat(p.price_per_gb) || 0.0;
  }

  computeValueScore(p, pricePerGB) {
    const price = pricePerGB || parseFloat(p.realPricePerGB) || 1.0;
    const rating = parseFloat(p.rating) || 3.0; // fallback rating
    const uptime = parseFloat(p.uptime_percentage) || 98.0;
    
    // higher value score is better!
    return (1.0 / price) * rating * (uptime / 100.0);
  }

  get results() {
    const { pricing, providers } = this.parsedData;
    const pricingInfo = pricing[this.proxyType];
    if (!pricingInfo) return null;

    const bandwidth = this.bandwidth;
    const connections = this.connections;
    
    let regionMult = 1.0;
    if (this.regions.length > 0) {
      let total = 0;
      this.regions.forEach((r) => {
        total += pricingInfo.regionMultipliers[r] || 1.0;
      });
      regionMult = total / this.regions.length;
    }

    const tierBreakdown = [];
    let remaining = bandwidth;
    let totalCost = 0;
    let bestPricePerGB = Infinity;
    let worstPricePerGB = 0;

    for (let t = 0; t < pricingInfo.tiers.length; t++) {
      const tier = pricingInfo.tiers[t];
      if (remaining <= 0) break;
      const tierCapacity = (tier.maxGB === null || tier.maxGB === undefined) ? Infinity : (tier.maxGB - tier.minGB + 1);
      const usedInTier = Math.min(remaining, tierCapacity);
      const tierCost = usedInTier * tier.pricePerGB * regionMult;
      tierBreakdown.push({
        label: `${usedInTier} GB @ ${this.formatCurrency(tier.pricePerGB)}/GB`,
        pricePerGB: tier.pricePerGB * regionMult,
        cost: tierCost,
        volume: usedInTier,
        maxVolume: tierCapacity,
      });
      totalCost += tierCost;
      if (tier.pricePerGB * regionMult < bestPricePerGB) {
        bestPricePerGB = tier.pricePerGB * regionMult;
      }
      if (tier.pricePerGB * regionMult > worstPricePerGB) {
        worstPricePerGB = tier.pricePerGB * regionMult;
      }
      remaining -= usedInTier;
    }

    let connectionSurcharge = 0;
    if (connections > pricingInfo.connectionSurchargeThreshold) {
      connectionSurcharge =
        (connections - pricingInfo.connectionSurchargeThreshold) *
        pricingInfo.connectionSurchargePerConn;
    }
    const totalMonthly = totalCost + connectionSurcharge;
    const costPerGB = bandwidth > 0 ? totalMonthly / bandwidth : 0;

    const typeProviders = providers[this.proxyType] || [];
    
    // Find active tier label for provider tier matching
    const activeTier = pricingInfo.tiers.find(t => bandwidth >= t.minGB && (t.maxGB === null || bandwidth <= t.maxGB));
    const activeTierLabel = activeTier ? activeTier.tier : "Starter";

    // Map each provider to its actual pricing under the current parameters
    const calculatedProviders = typeProviders.map((p) => {
      const providerPricePerGB = this.getProviderPricePerGB(p, bandwidth, activeTierLabel);
      const providerMonthly = (providerPricePerGB * bandwidth * regionMult) + connectionSurcharge;
      const valueScore = this.computeValueScore(p, providerPricePerGB);

      return {
        ...p,
        monthly: providerMonthly,
        perGB: providerPricePerGB,
        valueScore: valueScore
      };
    });

    let selectedProviderData = null;
    let adjustedTotal = totalMonthly;
    if (this.selectedProvider) {
      selectedProviderData = calculatedProviders.find((p) => p.name === this.selectedProvider);
      if (selectedProviderData) {
        adjustedTotal = selectedProviderData.monthly;
      }
    }

    // Recommended cards: rank by composite value score descending (best value first)
    const recommended = calculatedProviders.slice()
      .sort((a, b) => b.valueScore - a.valueScore)
      .slice(0, 3)
      .map((p, idx) => {
        return {
          rank: idx + 1,
          logo_url: p.logo_url,
          slug: p.slug,
          name: p.name,
          quality: p.quality,
          features: p.features,
          monthly: p.monthly,
          perGB: p.perGB,
          valueScore: p.valueScore
        };
      });

    // Comparison table: sort by total monthly cost ascending (cheapest first)
    const sortedProviders = calculatedProviders.slice()
      .sort((a, b) => a.monthly - b.monthly);

    return {
      totalCost,
      totalMonthly,
      costPerGB,
      connectionSurcharge,
      bestPricePerGB,
      worstPricePerGB,
      tierBreakdown,
      recommended,
      allProviders: sortedProviders,
      selectedProvider: selectedProviderData,
      adjustedTotal,
    };
  }

  get displayMonthly() {
    const results = this.results;
    if (!results) return "—";
    const target = results.selectedProvider ? results.adjustedTotal : results.totalMonthly;
    return this.formatCurrency(target);
  }

  get displayMonthlyRange() {
    const results = this.results;
    if (!results) return "—";
    if (results.selectedProvider) {
      return `Based on ${results.selectedProvider.name} pricing`;
    }
    return `${this.formatCurrency(results.totalMonthly * 0.85)} — ${this.formatCurrency(results.totalMonthly * 1.25)}`;
  }

  get displayPerGB() {
    const results = this.results;
    if (!results) return "—";
    
    let costPerGb = results.costPerGB;
    if (results.selectedProvider && this.bandwidth > 0) {
      costPerGb = results.adjustedTotal / this.bandwidth;
    }
    return this.formatCurrency(costPerGb);
  }

  get displayPerGBRange() {
    const results = this.results;
    if (!results) return "—";
    if (results.selectedProvider) {
      return `Based on ${results.selectedProvider.name} pricing`;
    }
    return `${this.formatCurrency(results.bestPricePerGB)} — ${this.formatCurrency(results.worstPricePerGB)}`;
  }

  get displayConnectionCost() {
    const results = this.results;
    if (!results) return "—";
    return this.formatCurrency(results.connectionSurcharge);
  }

  get displayConnectionNote() {
    const results = this.results;
    if (!results) return "";
    const pricingInfo = this.parsedData.pricing[this.proxyType];
    if (!pricingInfo) return "";
    return results.connectionSurcharge > 0
      ? `For ${this.connections} connections (above ${pricingInfo.connectionSurchargeThreshold} threshold)`
      : `No surcharge at ${this.connections} connections`;
  }

  // Region multiplier displayed as a chip — makes the multiplier visible
  // rather than buried in the math.
  get regionMultiplierLabel() {
    const results = this.results;
    if (!results || !results.totalCost || results.totalCost === 0) return "1.0";
    return (results.totalMonthly / results.totalCost).toFixed(2);
  }

  // Savings vs average of all providers — shows how much the top
  // recommendation saves compared to the mean.
  get savingsVsAverage() {
    const results = this.results;
    if (!results || !results.calculatedProviders || results.calculatedProviders.length < 2) return null;
    // Use each provider's actual computed monthly cost, not a scaled ratio
    const costs = results.calculatedProviders.map((p) => p.monthly);
    const avg = costs.reduce((a, b) => a + b, 0) / costs.length;
    const best = results.recommended[0];
    if (!best || avg === 0) return null;
    const savings = ((avg - best.monthly) / avg) * 100;
    return savings > 1 ? Math.round(savings) + "%" : null;
  }

  // Cost curve SVG — computes monthly cost at 20 bandwidth points (1 → 10000)
  // and renders as an inline polyline. Pure SVG, no charting library.
  get costCurvePoints() {
    const { pricing } = this.parsedData;
    const pricingInfo = pricing[this.proxyType];
    if (!pricingInfo || !pricingInfo.tiers || pricingInfo.tiers.length === 0) return "";

    const maxBw = 10000;
    const numPoints = 20;
    const points = [];

    // Shared reference price for curve + dot consistency
    const refPrice = pricingInfo.tiers[0]?.pricePerGB || 1;

    for (let i = 0; i <= numPoints; i++) {
      const bw = Math.max(1, Math.round((maxBw / numPoints) * i));
      const tier = pricingInfo.tiers.find((t) => bw >= t.minGB && (!t.maxGB || bw <= t.maxGB))
                   || pricingInfo.tiers[pricingInfo.tiers.length - 1];
      const cost = tier ? bw * tier.pricePerGB : 0;
      const x = (i / numPoints) * 400;
      const y = 110 - Math.min(cost / (maxBw * refPrice) * 100, 100);
      points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
    return points.join(" ");
  }

  get costCurveCurrentX() {
    return ((this.bandwidth / 10000) * 400).toFixed(1);
  }

  get costCurveCurrentY() {
    const results = this.results;
    if (!results) return 110;
    // Same scale as the curve points: normalize against max possible cost.
    const maxMonthly = 10000 * (this.parsedData.pricing[this.proxyType]?.tiers?.[0]?.pricePerGB || 1); // same refPrice as curve
    const targetCost = results.selectedProvider ? results.adjustedTotal : results.totalMonthly;
    const ratio = maxMonthly > 0 ? targetCost / maxMonthly : 0;
    return (110 - Math.min(ratio * 100, 100)).toFixed(1);
  }

  get tierBreakdownBars() {
    const results = this.results;
    if (!results) return [];

    let maxCost = 0;
    results.tierBreakdown.forEach((t) => {
      if (t.cost > maxCost) maxCost = t.cost;
    });
    if (maxCost === 0) maxCost = 1;

    return results.tierBreakdown.map((t) => {
      const pct = (t.cost / maxCost) * 100;
      let fillClass = "proxyviews-calc-bar-fill--low";
      if (t.pricePerGB > 5) fillClass = "proxyviews-calc-bar-fill--high";
      else if (t.pricePerGB > 2) fillClass = "proxyviews-calc-bar-fill--mid";

      return {
        label: t.label,
        pct,
        style: htmlSafe(`width: ${pct}%`),
        fillClass,
        costFormatted: this.formatCurrency(t.cost)
      };
    });
  }

  get comparisonRows() {
    const results = this.results;
    if (!results) return [];

    const providers = this.sortedComparisonProviders;
    const bandwidth = this.bandwidth;

    // Derive the effective region multiplier from the headline estimate
    // so the comparison table is consistent with the cards above it.
    const regionMult = results.totalCost > 0
      ? results.totalMonthly / results.totalCost
      : 1.0;

    const activeTier = this.parsedData.pricing[this.proxyType]?.tiers?.find(t => bandwidth >= t.minGB && (t.maxGB === null || bandwidth <= t.maxGB));
    const activeTierLabel = activeTier ? activeTier.tier : "Starter";

    return COMPARISON_FEATURES.map((feature) => {
      const cols = providers.map((p) => {
        const pPricePerGB = this.getProviderPricePerGB(p, bandwidth, activeTierLabel);
        const hasRealPrice = pPricePerGB > 0;
        const providerMult = p.price_multiplier || p.multiplier || 1.0;

        let val = "";
        let rawVal = 0;

        if (feature.key === "bandwidthCost") {
          if (hasRealPrice) {
            rawVal = bandwidth * pPricePerGB * regionMult;
          } else {
            rawVal = results.totalCost * providerMult;
          }
          val = this.formatCurrency(rawVal);
        } else if (feature.key === "costPerGB") {
          const unit = p.pricingUnit || "per_gb";
          if (hasRealPrice) {
            rawVal = pPricePerGB * regionMult;
          } else {
            rawVal = bandwidth > 0 ? (results.totalCost * providerMult) / bandwidth : 0;
          }
          val = this.formatCurrency(rawVal) + (unit === "per_ip" ? "/IP" : "/GB");
        } else if (feature.key === "connectionSurcharge") {
          rawVal = results.connectionSurcharge;
          val = this.formatCurrency(rawVal);
        } else if (feature.key === "totalMonthly") {
          if (hasRealPrice) {
            rawVal = (bandwidth * pPricePerGB * regionMult) + results.connectionSurcharge;
          } else {
            rawVal = results.totalMonthly * providerMult;
          }
          val = this.formatCurrency(rawVal);
        } else if (feature.key === "quality") {
          val = p.quality || p.verification_tier || "—";
        } else if (feature.key === "features") {
          val = p.features || (p.supported_protocols ? p.supported_protocols.join(", ") : "—");
        }

        return { val, raw: rawVal };
      });

      let minRaw = Infinity;
      if (["totalMonthly", "costPerGB", "bandwidthCost"].includes(feature.key)) {
        cols.forEach((col) => {
          if (col.raw > 0 && col.raw < minRaw) minRaw = col.raw;
        });
      }

      const colsWithBest = cols.map((col) => ({
        ...col,
        isBest: col.raw === minRaw && minRaw > 0 && minRaw !== Infinity
      }));

      return {
        label: feature.label,
        key: feature.key,
        cols: colsWithBest
      };
    });
  }

  // Dynamically sort comparison providers by their calculated monthly cost
  // (cheapest first) based on current bandwidth, region, and connections.
  // This makes the table columns re-order themselves as the user drags
  // the slider — the best-value provider is always first.
  get sortedComparisonProviders() {
    const results = this.results;
    if (!results) return this.availableProviders || [];

    const bandwidth = this.bandwidth;
    const regionMult = results.totalCost > 0
      ? results.totalMonthly / results.totalCost
      : 1.0;

    const activeTier = this.parsedData.pricing[this.proxyType]?.tiers?.find(t => bandwidth >= t.minGB && (t.maxGB === null || bandwidth <= t.maxGB));
    const activeTierLabel = activeTier ? activeTier.tier : "Starter";

    return (this.availableProviders || []).slice().map((p) => {
      const pPricePerGB = this.getProviderPricePerGB(p, bandwidth, activeTierLabel);
      const hasRealPrice = pPricePerGB > 0;
      const providerMult = p.price_multiplier || p.multiplier || 1.0;
      const cost = hasRealPrice
        ? (bandwidth * pPricePerGB * regionMult) + results.connectionSurcharge
        : results.totalMonthly * providerMult;
      return { ...p, _calcCost: cost };
    }).sort((a, b) => (a._calcCost || 0) - (b._calcCost || 0))
      .slice(0, 5);
  }

  // Rank labels for the comparison header — "Best Value", "#2", etc.
  get comparisonProviderRanks() {
    return this.sortedComparisonProviders.map((p, idx) => {
      if (idx === 0) return "★ Best Value";
      return `#${idx + 1}`;
    });
  }


  @action handleLogoError(e) {
    e.target.style.display = "none";
  }

  @action changeProxyType(e) {
    this.proxyType = e.target.value;
    this.selectedProvider = "";
  }

  // Debounce slider updates so the expensive `results` getter (which loops
  // all providers, sorts, and maps) doesn't recompute on every pixel of
  // slider drag. 150ms matches the directory browser's debounce pattern.
  _bandwidthTimer = null;
  _connectionsTimer = null;

  @action updateBandwidth(e) {
    const val = parseInt(e.target.value, 10);
    const safe = isNaN(val) ? 0 : val;
    clearTimeout(this._bandwidthTimer);
    this._bandwidthTimer = setTimeout(() => { this.bandwidth = safe; }, 150);
  }

  @action updateConnections(e) {
    const val = parseInt(e.target.value, 10);
    const safe = isNaN(val) ? 0 : val;
    clearTimeout(this._connectionsTimer);
    this._connectionsTimer = setTimeout(() => { this.connections = safe; }, 150);
  }

  @action updateSelectedProvider(e) {
    this.selectedProvider = e.target.value;
  }

  @action toggleRegion(regionCode, e) {
    if (e.target.checked) {
      if (!this.regions.includes(regionCode)) {
        this.regions = [...this.regions, regionCode];
      }
    } else {
      this.regions = this.regions.filter(r => r !== regionCode);
    }
  }

  @action toggleAutoCalc(e) {
    this.autoCalc = e.target.checked;
  }

  @action resetDefaults() {
    this.bandwidth = 100;
    this.proxyType = "residential";
    this.connections = 100;
    this.regions = ["NA"];
    this.selectedProvider = "";
    this.autoCalc = true;
  }

  @action submitForm(e) {
    e.preventDefault();
  }

  <template>
    <div class="proxyviews-calc-wrapper" data-enabled="true">
      <div class="proxyviews-calc-container">
        <div class="proxyviews-calc-header">
          <h2 class="proxyviews-calc-title">Proxy Cost Estimator</h2>
          <p class="proxyviews-calc-subtitle">Compare real-time pricing across proxy providers</p>
        </div>

        <form id="proxyviews-calc-form" class="proxyviews-calc-form" autocomplete="off" {{on "submit" this.submitForm}}>
          <div class="proxyviews-calc-form-grid">
            <div class="proxyviews-calc-field proxyviews-calc-field--bandwidth">
              <label for="proxyviews-bandwidth" class="proxyviews-calc-label">
                Monthly Bandwidth
                <span id="proxyviews-bandwidth-value" class="proxyviews-calc-label-value">{{this.bandwidth}} GB</span>
              </label>
              <div class="proxyviews-calc-slider-row">
                <input
                  type="range"
                  id="proxyviews-bandwidth"
                  name="bandwidth"
                  min="1"
                  max="10000"
                  step="1"
                  value={{this.bandwidth}}
                  class="proxyviews-calc-slider"
                  {{on "input" this.updateBandwidth}}
                />
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={{this.bandwidth}}
                  class="proxyviews-calc-number-input"
                  aria-label="Bandwidth in GB"
                  {{on "input" this.updateBandwidth}}
                />
              </div>
              <div class="proxyviews-calc-slider-ticks">
                <span>1 GB</span>
                <span>2,500 GB</span>
                <span>5,000 GB</span>
                <span>7,500 GB</span>
                <span>10,000 GB</span>
              </div>
            </div>

            <div class="proxyviews-calc-field proxyviews-calc-field--type">
              <label for="proxyviews-proxy-type" class="proxyviews-calc-label">Proxy Type</label>
              <select id="proxyviews-proxy-type" name="proxy_type" class="proxyviews-calc-select" {{on "change" this.changeProxyType}}>
                <option value="residential" selected={{eq this.proxyType "residential"}}>Residential</option>
                <option value="datacenter" selected={{eq this.proxyType "datacenter"}}>Datacenter</option>
                <option value="mobile" selected={{eq this.proxyType "mobile"}}>Mobile / 4G/5G</option>
                <option value="isp" selected={{eq this.proxyType "isp"}}>ISP (Static Residential)</option>
                <option value="rotating" selected={{eq this.proxyType "rotating"}}>Rotating</option>
              </select>
            </div>

            <div class="proxyviews-calc-field proxyviews-calc-field--provider">
              <label for="proxyviews-provider-select" class="proxyviews-calc-label">
                Specific Provider
                <span id="proxyviews-provider-count" class="proxyviews-calc-badge">{{this.availableProviders.length}} providers</span>
              </label>
              <select id="proxyviews-provider-select" name="provider" class="proxyviews-calc-select" {{on "change" this.updateSelectedProvider}}>
                <option value="" selected={{eq this.selectedProvider ""}}>All Providers (Auto-Recommend)</option>
                {{#each this.availableProviders as |p|}}
                  <option value={{p.name}} selected={{eq this.selectedProvider p.name}}>{{p.name}} ({{p.quality}})</option>
                {{/each}}
              </select>
            </div>

            <div class="proxyviews-calc-field proxyviews-calc-field--connections">
              <label for="proxyviews-connections" class="proxyviews-calc-label">
                Concurrent Connections
                <span id="proxyviews-connections-value" class="proxyviews-calc-label-value">{{this.connections}}</span>
              </label>
              <div class="proxyviews-calc-slider-row">
                <input
                  type="range"
                  id="proxyviews-connections"
                  name="connections"
                  min="1"
                  max="1000"
                  step="1"
                  value={{this.connections}}
                  class="proxyviews-calc-slider"
                  {{on "input" this.updateConnections}}
                />
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={{this.connections}}
                  class="proxyviews-calc-number-input"
                  aria-label="Number of concurrent connections"
                  {{on "input" this.updateConnections}}
                />
              </div>
              <div class="proxyviews-calc-slider-ticks">
                <span>1</span>
                <span>250</span>
                <span>500</span>
                <span>750</span>
                <span>1,000</span>
              </div>
            </div>

            <div class="proxyviews-calc-field proxyviews-calc-field--region">
              <label class="proxyviews-calc-label">Region Preference</label>
              <div id="proxyviews-region-options" class="proxyviews-calc-checkbox-group">
                {{#each this.activeRegions as |reg|}}
                  <label class="proxyviews-calc-checkbox-label">
                    <input type="checkbox" name="region" value={{reg.code}} checked={{includes this.regions reg.code}} {{on "change" (fn this.toggleRegion reg.code)}} />
                    <span class="proxyviews-calc-checkbox-custom"></span>
                    {{reg.name}}
                  </label>
                {{else}}
                  <label class="proxyviews-calc-checkbox-label">
                    <input type="checkbox" name="region" value="Global" checked={{includes this.regions "Global"}} {{on "change" (fn this.toggleRegion "Global")}} />
                    <span class="proxyviews-calc-checkbox-custom"></span>
                    Global (Any)
                  </label>
                {{/each}}
              </div>
            </div>

            <div class="proxyviews-calc-field proxyviews-calc-field--autocalc">
              <div class="proxyviews-calc-label">
                <span>Auto-Calculate</span>
                <span class="proxyviews-calc-toggle" role="checkbox" aria-checked={{this.autoCalc}} tabindex="0">
                  <input type="checkbox" id="proxyviews-auto-calc" checked={{this.autoCalc}} {{on "change" this.toggleAutoCalc}} />
                  <span class="proxyviews-calc-toggle-track"></span>
                </span>
              </div>
              <p class="proxyviews-calc-field-hint">Results update automatically as you adjust inputs</p>
            </div>
          </div>

          <div class="proxyviews-calc-actions">
            <button type="button" id="proxyviews-calc-reset" class="proxyviews-calc-btn proxyviews-calc-btn--secondary" {{on "click" this.resetDefaults}}>
              Reset Defaults
            </button>
          </div>
        </form>

        {{#if this.results}}
          <div id="proxyviews-calc-results" class="proxyviews-calc-results">
            <h3 class="proxyviews-calc-section-title">Estimated Costs</h3>

            <div class="proxyviews-calc-summary-cards">
              <div class="proxyviews-calc-card proxyviews-calc-card--monthly">
                <div class="proxyviews-calc-card-label">Estimated Monthly</div>
                <div id="proxyviews-result-monthly" class="proxyviews-calc-card-value">{{this.displayMonthly}}</div>
                <div id="proxyviews-result-range" class="proxyviews-calc-card-range">{{this.displayMonthlyRange}}</div>
              </div>
              <div class="proxyviews-calc-card proxyviews-calc-card--pergb">
                <div class="proxyviews-calc-card-label">{{if (eq this.currentPricingUnit "per_ip") "Cost per IP" "Cost per GB"}}</div>
                <div id="proxyviews-result-pergb" class="proxyviews-calc-card-value">{{this.displayPerGB}}</div>
                <div id="proxyviews-result-pergb-range" class="proxyviews-calc-card-range">{{this.displayPerGBRange}}</div>
              </div>
              <div class="proxyviews-calc-card proxyviews-calc-card--connections">
                <div class="proxyviews-calc-card-label">Connection Surcharge</div>
                <div id="proxyviews-result-conn-cost" class="proxyviews-calc-card-value">{{this.displayConnectionCost}}</div>
                <div id="proxyviews-result-conn-note" class="proxyviews-calc-card-range">{{this.displayConnectionNote}}</div>
              </div>
            </div>

            <div class="proxyviews-calc-meta-chips">
              {{#each this.regions as |r|}}
                <span class="proxyviews-calc-chip">{{r}}</span>
              {{/each}}
              <span class="proxyviews-calc-chip proxyviews-calc-chip--accent">Region factor: {{this.regionMultiplierLabel}}x</span>
              {{#if this.savingsVsAverage}}
                <span class="proxyviews-calc-chip proxyviews-calc-chip--success">Save {{this.savingsVsAverage}} vs avg</span>
              {{/if}}
            </div>

            <div class="proxyviews-calc-cost-curve">
              <h4 class="proxyviews-calc-section-title">Cost Curve (Monthly vs Bandwidth)</h4>
              <svg viewBox="0 0 400 120" class="proxyviews-calc-svg-chart" preserveAspectRatio="none">
                <polyline points={{this.costCurvePoints}} fill="none" stroke="var(--tertiary)" stroke-width="2" />
                <circle cx={{this.costCurveCurrentX}} cy={{this.costCurveCurrentY}} r="4" fill="var(--tertiary)" />
              </svg>
              <div class="proxyviews-calc-cost-curve-labels">
                <span>1 GB</span>
                <span>{{this.bandwidth}} GB (you)</span>
                <span>10,000 GB</span>
              </div>
            </div>

            <div class="proxyviews-calc-breakdown">
              <h4 class="proxyviews-calc-section-title">Price Breakdown by Tier</h4>
              <div id="proxyviews-breakdown-bars" class="proxyviews-calc-breakdown-bars">
                {{#each this.tierBreakdownBars as |bar|}}
                  <div class="proxyviews-calc-bar-row">
                    <div class="proxyviews-calc-bar-label">{{bar.label}}</div>
                    <div class="proxyviews-calc-bar-track">
                      <div class="proxyviews-calc-bar-fill {{bar.fillClass}}" style={{bar.style}}></div>
                    </div>
                    <div class="proxyviews-calc-bar-price">{{bar.costFormatted}}</div>
                  </div>
                {{/each}}
              </div>
            </div>

            <div class="proxyviews-calc-recommendations">
              <h4 class="proxyviews-calc-section-title">Top 3 Recommended Providers</h4>
              <div id="proxyviews-recommendations" class="proxyviews-calc-recommendations-list">
                {{#each this.results.recommended as |p|}}
                  <a class="proxyviews-calc-provider-card" href="/providers/{{p.slug}}">
                    {{#if p.logo_url}}
                      <img src={{p.logo_url}} alt={{p.name}} class="proxyviews-calc-provider-logo" loading="lazy" {{on "error" this.handleLogoError}} />
                    {{/if}}
                    <div class="proxyviews-calc-provider-rank">#{{p.rank}} Best Value</div>
                    <div class="proxyviews-calc-provider-name">{{p.name}}</div>
                    <div class="proxyviews-calc-provider-price">{{this.formatCurrency p.monthly}}/mo</div>
                    <div class="proxyviews-calc-provider-detail">
                      {{this.formatCurrency p.perGB}}{{if (eq p.pricingUnit "per_ip") "/IP" "/GB"}} &middot; {{p.quality}} &middot; {{p.features}}
                    </div>
                  </a>
                {{/each}}
              </div>
            </div>

            <div id="proxyviews-calc-comparison-section" class="proxyviews-calc-comparison">
              <h4 class="proxyviews-calc-section-title">
                Vendor Comparison
                <span id="proxyviews-comparison-count" class="proxyviews-calc-badge">{{this.sortedComparisonProviders.length}} vendors · sorted by best value</span>
              </h4>

                <div class="proxyviews-calc-table-wrap">
                  <table id="proxyviews-comparison-table" class="proxyviews-calc-table">
                    <thead id="proxyviews-comparison-thead">
                      <tr>
                        <th>Feature</th>
                        {{#each this.sortedComparisonProviders as |prov index|}}
                          <th>
                            <div class="proxyviews-calc-th-rank">{{get this.comparisonProviderRanks index}}</div>
                            {{#if prov.logo_url}}
                              <img src={{prov.logo_url}} alt={{prov.name}} class="proxyviews-calc-th-logo" loading="lazy" {{on "error" this.handleLogoError}} />
                            {{/if}}
                            <a href="/providers/{{prov.slug}}" class="proxyviews-calc-th-link">{{prov.name}}</a>
                          </th>
                        {{/each}}
                      </tr>
                    </thead>
                    <tbody id="proxyviews-comparison-tbody">
                      {{#each this.comparisonRows as |row|}}
                        <tr>
                          <td><strong>{{row.label}}</strong></td>
                          {{#each row.cols as |col|}}
                            <td class={{if col.isBest "proxyviews-best-price"}}>{{col.val}}</td>
                          {{/each}}
                        </tr>
                      {{/each}}
                    </tbody>
                  </table>
                </div>
            </div>

            <div class="proxyviews-calc-cta-section">
              <p class="proxyviews-calc-cta-text">Not sure which provider fits your use case? Compare them side-by-side or browse the full directory.</p>
              <div class="proxyviews-calc-cta-actions">
                <a class="proxyviews-calc-btn proxyviews-calc-btn--primary" href="/providers/compare">
                  Compare Providers
                </a>
                <a class="proxyviews-calc-btn proxyviews-calc-btn--secondary" href="/providers">
                  Browse Full Directory
                </a>

              </div>
            </div>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
